import * as React from 'react';
import { connect } from 'react-redux';
import { flatten, isEmpty, isNaN } from 'lodash';

import { PromptExit } from 'libs/router';

import { Form, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';

import DeliverableMediaAttachmentsForDeliverable from 'modules/deliverables/components/media/parts/DeliverableMediaAttachmentsForDeliverable';
import DeliverableMediaApprovalContextMenu from 'modules/deliverables/components/media/parts/context-menu/DeliverableMediaApprovalContextMenu';
import DeliverableMediaUploader from 'modules/deliverables/components/media/parts/DeliverableMediaUploader';
import ContentApproval from 'modules/campaigns/components/content-approval/ContentApproval';

import ClickTutorialHandler from 'modules/help-menu/helpers/ClickTutorialHandler';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { createDeliverableAttachmentsBatch } from 'modules/deliverables/reducers/deliverableMediaAttachments';
import { fetchContentApprovalData } from 'modules/campaigns/reducers/contentApproval';
import {
  changeAttachmentsApprovalStatus,
  changeDeliverableApprovalNote,
  deliverableAttachmentApprovalStatusModified
} from 'modules/subcampaigns/reducers/offerDeliverables';

import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import {
  DELIVERABLE_ATTACHMENT_TYPE_HIGHRES,
  scrollToDeliverable
} from 'utils/deliverables';
import { __t, applyTranslations } from 'utils/language';
import { VideoKind } from 'modules/help-menu/util/videos';
import { APPROVAL_STATUSES } from 'utils/contentApproval';

import type {
  ClientApprovalBucket,
  Deliverable,
  DeliverableAttachment,
  DeliverableAttachmentApprovalStatus,
  DeliverableUploadedEventAction,
  Subcampaign,
  TextEntry
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./deliverable-content-production.i18n.json'));

type OwnProps = {
  deliverablesContainerRef: React.RefObject<any>,
  canApprove: boolean,
  canUpload: boolean,
  subcampaign: Subcampaign,
  deliverable: Deliverable,
  onApprovalMode: () => void,
  onUploadModeToggled?: () => void,
  onDownloadAttachments: (b?: string | null, a?: boolean | null) => void,
  onTrackDeliverableUploadEvent: (eventAction: DeliverableUploadedEventAction) => void
};

type StateProps = {
  buckets: Array<ClientApprovalBucket>
};

type DispatchProps = {
  dispatchFetchContentApprovalData: (c: number, b: number, a: string) => void,
  dispatchCreateAttachmentsBatch: (c: number, b: number, a: Array<DeliverableAttachment>) => Promise<void>,
  dispatchChangeDeliverableApprovalNote: (d: number, c: number, b: string, a: string) => Promise<void>,
  dispatchAttachmentApprovalStatusModified: (c: number, b: number, a: DeliverableAttachment) => Promise<void>,
  dispatchChangeAttachmentApprovalStatus: (c: number, b: number, a: Array<DeliverableAttachment>) => Promise<void>,
  dispatchShowConfirmationModal: (
    params: {
      message: string,
      title?: string,
      confirmButtonLabel?: string,
      cancelButtonLabel?: string,
      onConfirm?: () => void,
      onCancel?: () => void
    }
  ) => Promise<void>,
  dispatchHideConfirmationModal: () => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  brandFeedback: string,
  creatorNote: string,
  isNoteModified: boolean,
  isApprovalStatusChanged: boolean,
  startFromViewMode: boolean,
  contentSortMode: boolean
};

type Mode = 'view-by-influencer' | 'creation-by-influencer' | 'creation-by-brand' | 'approval-by-brand';

const Modes: { [mode: string]: Mode} = {
  VIEW_BY_INFLUENCER: 'view-by-influencer',
  CREATION_BY_INFLUENCER: 'creation-by-influencer',
  CREATION_BY_BRAND: 'creation-by-brand',
  APPROVAL_BY_BRAND: 'approval-by-brand'
};

const msgKey = 'deliverable_content_production';

class DeliverableMediaContentProductionForApproval extends React.PureComponent<Props, State> {
  state = {
    brandFeedback: this.props.deliverable.brandFeedback || '',
    creatorNote: this.props.deliverable.creatorNote || '',
    isNoteModified: false,
    isApprovalStatusChanged: false,
    startFromViewMode: (this.props.deliverable.postAttachments || []).length > 0,
    contentSortMode: false
  };

  componentDidMount() {
    const { subcampaign, deliverable, canApprove, dispatchFetchContentApprovalData } = this.props;

    if (canApprove) {
      dispatchFetchContentApprovalData(subcampaign.campaignId, deliverable.id, 'created_at.asc');
    }
  }

  hasClientAddedNewFeedback(): boolean {
    const { deliverable, buckets } = this.props;

    if (isEmpty(buckets)) {
      return false;
    }

    return (buckets || []).some(bucket => (
      bucket.items?.some(item => {
        if (item.approvalStatus !== APPROVAL_STATUSES.PENDING) {
          const foundItem = deliverable.postAttachments?.find(pa => pa.id === item.attachmentId);

          return item.approvalStatus !== foundItem?.approvalStatus;
        }

        return false;
      })
    ));
  }

  getTmpAttachments(): Array<DeliverableAttachment> {
    const { deliverable } = this.props;

    return (deliverable.postAttachments || []).filter(attachment => isNaN(Number(attachment.id)));
  }

  hasContentStatusBeenChanged(): boolean {
    const { deliverable } = this.props;

    return (deliverable.postAttachments || []).some(
      attachment => attachment.approvalStatus !== APPROVAL_STATUSES.PENDING
    );
  }

  isApprovalButtonDisabled(): boolean {
    const { canApprove } = this.props;
    const { isApprovalStatusChanged } = this.state;

    return !canApprove || !isApprovalStatusChanged;
  }

  isCreationButtonDisabled(): boolean {
    const tmpAttachments = this.getTmpAttachments();
    const isModified = tmpAttachments.length > 0;

    return !isModified;
  }

  finishSendRequest(message: string) {
    const { dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;

    this.setState(() => ({
      isNoteModified: false,
      isApprovalStatusChanged: false,
      startFromViewMode: true
    }));

    dispatchShowConfirmationModal({
      title: __t(`${msgKey}.sent_modal_title`),
      message,
      confirmButtonLabel: __t(`${msgKey}.ok_button_label`),
      onConfirm: () => dispatchHideConfirmationModal()
    });
  }

  handleUploadTracking = (eventAction: DeliverableUploadedEventAction): void => {
    const { onTrackDeliverableUploadEvent, onUploadModeToggled } = this.props;
    const { isApprovalStatusChanged } = this.state;
    const tmpAttachments = this.getTmpAttachments();

    onTrackDeliverableUploadEvent(eventAction);

    if (onUploadModeToggled && !isApprovalStatusChanged && tmpAttachments.length === 0) {
      onUploadModeToggled();
    }
  };

  handleApprovalStatusChanged = (
    attachment: DeliverableAttachment,
    approvalStatus: DeliverableAttachmentApprovalStatus
  ): void => {
    const { subcampaign, deliverable, dispatchAttachmentApprovalStatusModified } = this.props;

    dispatchAttachmentApprovalStatusModified(subcampaign.offer.id, deliverable.id, {
      ...attachment,
      approvalStatus
    });

    this.setState({
      isApprovalStatusChanged: true
    });
  };

  handleApplyClientFeedback = (deliverable: Deliverable): void => {
    if (this.hasContentStatusBeenChanged()) {
      this.handleApplyClientFeedbackModal(deliverable);
      return;
    }

    this.handleApplyClientFeedbackApply(deliverable);
  };

  handleApplyClientFeedbackApply(deliverable: Deliverable): void {
    const { buckets } = this.props;
    const items = flatten((buckets || []).map(bucket => bucket.items));

    (deliverable.postAttachments || []).forEach(attachment => {
      const bucketItem = items.find(item => item.attachmentId === attachment.id);
      const updatedAttachment = {
        ...attachment,
        approvalStatus: bucketItem?.approvalStatus || APPROVAL_STATUSES.PENDING
      };

      this.handleApprovalStatusChanged(updatedAttachment, updatedAttachment.approvalStatus);
    });
  }

  handleApplyClientFeedbackModal(deliverable: Deliverable): void {
    const { dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;

    dispatchShowConfirmationModal({
      title: __t(`${msgKey}.apply_client_feedback.modal_title`),
      message: __t(`${msgKey}.apply_client_feedback.modal_body`),
      confirmButtonLabel: __t(`${msgKey}.apply_client_feedback.buttons.modal_approve`),
      cancelButtonLabel: __t(`${msgKey}.apply_client_feedback.buttons.modal_cancel`),
      onConfirm: () => {
        this.handleApplyClientFeedbackApply(deliverable);
        dispatchHideConfirmationModal();
      },
      onCancel: () => dispatchHideConfirmationModal()
    });
  }

  handleDownloadAttachments = (attachmentType?: string | null, approvedOnly?: boolean | null): void => {
    const { onDownloadAttachments } = this.props;
    onDownloadAttachments(attachmentType, approvedOnly);
  };

  handleResetApprovalStatus = (attachments: Array<DeliverableAttachment>): void => {
    attachments.forEach(attachment => this.handleApprovalStatusChanged(attachment, 'pending'));
  };

  handleInputChanged = (event: SyntheticEvent<TextEntry>) => {
    const { deliverable } = this.props;
    const target = event.currentTarget;
    const noteName = target.name;
    const noteValue = target.value;
    const previousNote = deliverable[noteName] || '';

    this.setState((prevState) => ({ ...prevState,
      [noteName]: noteValue,
      isNoteModified: noteValue !== previousNote
    }));
  };

  handleSubmitCreation = () => {
    const {
      canApprove,
      subcampaign,
      deliverable,
      dispatchCreateAttachmentsBatch,
      dispatchChangeDeliverableApprovalNote
    } = this.props;
    const { creatorNote } = this.state;
    const tmpAttachments = this.getTmpAttachments();

    Promise.resolve<false | Promise<void>>(!canApprove && dispatchChangeDeliverableApprovalNote(subcampaign.offer.id, deliverable.id, 'creatorNote', creatorNote))
      .then(() => {
        if (tmpAttachments.length > 0) {
          return dispatchCreateAttachmentsBatch(subcampaign.offer.id, deliverable.id, tmpAttachments);
        }
        return null;
      })
      .then(() => {
        this.finishSendRequest(__t(`${msgKey}.sent_message_for_influencer`));

        addUserAction({
          eventCategory: 'campaign-content',
          eventAction: 'content-added-for-review'
        });
      });
  };

  handleSubmitApproval = () => {
    const {
      subcampaign,
      deliverable,
      dispatchChangeAttachmentApprovalStatus,
      dispatchChangeDeliverableApprovalNote
    } = this.props;
    const { brandFeedback } = this.state;
    const attachments = deliverable.postAttachments || [];

    dispatchChangeDeliverableApprovalNote(subcampaign.offer.id, deliverable.id, 'brandFeedback', brandFeedback)
      .then(() => {
        if (attachments.length > 0) {
          return dispatchChangeAttachmentApprovalStatus(subcampaign.offer.id, deliverable.id, attachments);
        }
        return null;
      })
      .then(() => {
        this.finishSendRequest(__t(`${msgKey}.sent_message_for_brand`));

        addUserAction({
          eventCategory: 'campaign-content',
          eventAction: 'content-reviewed'
        });
      });
  };

  handleCancelViewMode = () => {
    this.setState({
      startFromViewMode: false
    });
  };

  handleContentSortModeToggle = () => {
    const { deliverable, onApprovalMode } = this.props;

    onApprovalMode();

    scrollToDeliverable(deliverable.id);

    this.setState((state) => ({
      ...state,
      contentSortMode: !state.contentSortMode
    }));
  };

  renderBrandFeedback(mode: Mode): React.ReactNode | undefined | null {
    const { brandFeedback } = this.state;

    if (mode === Modes.APPROVAL_BY_BRAND && !this.isApprovalButtonDisabled()) {
      return (
        <FormField
          direction="column"
          label={__t(`${msgKey}.brand_feedback_input_label`)}
          labelType="highlighted"
          fontSize="small"
          showValidationIcon={false}
          showRequiredMark={false}
        >
          <ClickTutorialHandler event={VideoKind.SHARE_INFORMATION_TEAM}>
            <Input
              name="brandFeedback"
              width="responsive"
              value={brandFeedback}
              multiline
              placeholder={__t(`${msgKey}.brand_feedback_input_placeholder`)}
              onChange={this.handleInputChanged}
            />
          </ClickTutorialHandler>
        </FormField>
      );
    }

    return brandFeedback ? (
      <FormField
        direction="column"
        label={__t(`${msgKey}.brand_feedback_label`)}
        labelType="highlighted"
        fontSize="small"
        showValidationIcon={false}
        showRequiredMark={false}
      >
        <div className="deliverable-media-content-production-for-approval__note-container">
          {brandFeedback}
        </div>
      </FormField>
    ) : null;
  }

  renderInfluencerNote(mode: Mode): React.ReactNode | undefined | null {
    const { creatorNote } = this.state;

    if (mode === Modes.CREATION_BY_INFLUENCER && !this.isCreationButtonDisabled()) {
      return (
        <FormField
          direction="column"
          label={__t(`${msgKey}.creator_note_input_label`)}
          labelType="highlighted"
          fontSize="small"
          showValidationIcon={false}
          showRequiredMark={false}
        >
          <Input
            name="creatorNote"
            width="responsive"
            value={creatorNote}
            multiline
            placeholder={__t(`${msgKey}.creator_note_input_placeholder`)}
            onChange={this.handleInputChanged}
          />
        </FormField>
      );
    }

    return creatorNote ? (
      <FormField
        direction="column"
        label={__t(`${msgKey}.creator_note_label`)}
        labelType="highlighted"
        fontSize="small"
        showValidationIcon={false}
        showRequiredMark={false}
      >
        <div className="deliverable-media-content-production-for-approval__note-container">
          {creatorNote}
        </div>
      </FormField>
    ) : null;
  }

  renderCreationButton(): React.ReactNode {
    return (
      <ActionButton
        key="creation-button"
        color="primary"
        disabled={this.isCreationButtonDisabled()}
        type="submit"
      >
        {__t(`${msgKey}.send_button_label`)}
      </ActionButton>
    );
  }

  renderApprovalButton(): React.ReactNode {
    return (
      <ActionButton
        key="approval-button"
        color="primary"
        disabled={this.isApprovalButtonDisabled()}
        type="submit"
      >
        {__t(`${msgKey}.send_button_label`)}
      </ActionButton>
    );
  }

  renderEditButton(): React.ReactNode {
    return (
      <ActionButton
        key="edit-button"
        color="secondary"
        onClick={this.handleCancelViewMode}
      >
        {__t(`${msgKey}.edit_button_label`)}
      </ActionButton>
    );
  }

  renderActionButton(mode: Mode): React.ReactNode {
    let actionButton: React.ReactNode = null;

    switch (mode) {
      case Modes.APPROVAL_BY_BRAND:
        actionButton = this.renderApprovalButton();
        break;
      case Modes.CREATION_BY_INFLUENCER:
      case Modes.CREATION_BY_BRAND:
        actionButton = this.renderCreationButton();
        break;
      case Modes.VIEW_BY_INFLUENCER:
        actionButton = this.renderEditButton();
        break;
      default:
        break;
    }

    return actionButton;
  }

  renderContextMenu(mode: Mode): React.ReactNode | undefined | null {
    const { deliverable, subcampaign, canUpload, canApprove, onUploadModeToggled } = this.props;

    return (
      <div className="deliverable-media-content-production-for-approval__footer-menu">
        <DeliverableMediaApprovalContextMenu
          isBulk
          subcampaign={subcampaign}
          deliverable={deliverable}
          canApprove={canApprove}
          canUpload={canUpload}
          attachments={deliverable.postAttachments || []}
          hasClientAddedNewFeedback={this.hasClientAddedNewFeedback()}
          onDownloadAttachments={this.handleDownloadAttachments}
          onUploadModeToggled={onUploadModeToggled}
          onResetApprovalStatus={mode === Modes.APPROVAL_BY_BRAND ? this.handleResetApprovalStatus : undefined}
          onContentSortModeToggle={this.handleContentSortModeToggle}
          onApplyClientFeedback={this.handleApplyClientFeedback}
          isEditMode={!this.isApprovalButtonDisabled()}
        />
      </div>
    );
  }

  renderApprovalForm(): React.ReactNode {
    const {
      canUpload,
      canApprove,
      subcampaign,
      deliverable
    } = this.props;

    const { startFromViewMode } = this.state;

    const attachments = deliverable.postAttachments || [];
    let mode = canApprove ? Modes.APPROVAL_BY_BRAND : Modes.CREATION_BY_INFLUENCER;

    if (mode === Modes.APPROVAL_BY_BRAND && canUpload) {
      mode = Modes.CREATION_BY_BRAND;
    }

    if (mode === Modes.CREATION_BY_INFLUENCER && startFromViewMode) {
      mode = Modes.VIEW_BY_INFLUENCER;
    }
    return (
      <React.Fragment>
        <DeliverableMediaAttachmentsForDeliverable
          canUpload={mode === Modes.VIEW_BY_INFLUENCER ? false : canUpload}
          canApprove={canApprove}
          subcampaign={subcampaign}
          deliverable={deliverable}
          placeholderForUpload={(
            <DeliverableMediaUploader
              size="big"
              offerId={subcampaign.offer.id}
              deliverable={deliverable}
              attachmentType={DELIVERABLE_ATTACHMENT_TYPE_HIGHRES}
              onUploadModeTracking={() => this.handleUploadTracking('content for approval')}
            />
          )}
          attachments={attachments}
          onApprovalStatusChanged={this.handleApprovalStatusChanged}
          onResetApprovalStatus={mode === Modes.APPROVAL_BY_BRAND ? this.handleResetApprovalStatus : undefined}
        />
        <div className="deliverable-media-content-production-for-approval__form">
          <Form
            onSubmit={mode === Modes.APPROVAL_BY_BRAND ? this.handleSubmitApproval : this.handleSubmitCreation}
          >
            <div className="deliverable-media-content-production-for-approval__form-field">
              {this.renderInfluencerNote(mode)}
            </div>
            <div className="deliverable-media-content-production-for-approval__form-field">
              {this.renderBrandFeedback(mode)}
            </div>
            <div className="deliverable-media-content-production-for-approval__footer">
              <div className="deliverable-media-content-production-for-approval__footer-action">
                {this.renderActionButton(mode)}
              </div>
              {this.renderContextMenu(mode)}
            </div>
          </Form>
        </div>
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { deliverablesContainerRef, subcampaign, deliverable } = this.props;
    const { isNoteModified, isApprovalStatusChanged, contentSortMode } = this.state;
    const isModified = isNoteModified || isApprovalStatusChanged || this.getTmpAttachments().length > 0;

    return (
      <section className="deliverable-media-content-production-for-approval">
        {
          isModified && (
            <PromptExit type="page" />
          )
        }
        {
          contentSortMode ? (
            <div className="deliverable-media-content-production-for-approval__content_sort">
              <ContentApproval
                deliverablesContainerRef={deliverablesContainerRef}
                campaignId={subcampaign.campaign.id}
                deliverableId={deliverable.id}
                onClose={this.handleContentSortModeToggle}
              />

            </div>
          ) : this.renderApprovalForm()
        }
      </section>
    );
  }
}

const mapStateToProps = ({ contentApproval }, ownProps: OwnProps): StateProps => {
  const deliverableState = contentApproval.deliverables[ownProps.deliverable?.id] || {};
  const { buckets = [] } = deliverableState;

  return {
    buckets: buckets.slice(1).filter(bucket => bucket.id > 0)
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchContentApprovalData: (campaignId: number, deliverableId: number, sort: string) => (
    dispatch(fetchContentApprovalData(campaignId, deliverableId, sort))
  ),

  dispatchCreateAttachmentsBatch: (offerId, deliverableId, attachments) =>
    dispatch(createDeliverableAttachmentsBatch(offerId, deliverableId, attachments)),

  dispatchChangeDeliverableApprovalNote: (offerId, deliverableId, noteName, note) =>
    dispatch(changeDeliverableApprovalNote(offerId, deliverableId, noteName, note)),

  dispatchAttachmentApprovalStatusModified: (offerId, deliverableId, attachment) =>
    dispatch(deliverableAttachmentApprovalStatusModified(offerId, deliverableId, attachment)),

  dispatchChangeAttachmentApprovalStatus: (offerId, deliverableId, attachments) =>
    dispatch(changeAttachmentsApprovalStatus(offerId, deliverableId, attachments)),

  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal())
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliverableMediaContentProductionForApproval);
